import {
  Injectable,
  Inject,
  Renderer2,
  RendererFactory2,
  Optional,
  ApplicationRef,
  NgZone,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { SocialLoginComponent } from './social.login.component';
import { ScriptService } from 'src/app/common/services/load.script/load.script.service';
import { ScriptLoader } from './script-loader.util';
import jwt_decode from 'jwt-decode';

@Injectable({
  providedIn: 'root',
})
export class CheckLoginStatusService {
  isUserLoggedIn =
    localStorage.getItem('social.login.googleUser') ||
    localStorage.getItem('social.login.facebookUser')
      ? true
      : false;
  profilePicture?: string =
    JSON.parse(
      localStorage.getItem('social.login.googleUser') ||
        localStorage.getItem('social.login.facebookUser') ||
        '{}'
    )?.profilePicture || '';
  provider?: string = localStorage.getItem('social.login.googleUser')
    ? 'google'
    : localStorage.getItem('social.login.facebookUser')
    ? 'facebook'
    : '';

  // Google specific properties
  public isGoogleInitialized = false;
  private googleLibraryLoaded = false;
  private renderer: Renderer2;
  private readonly GOOGLE_SCRIPT_PATH = 'https://accounts.google.com/gsi/client';
  private readonly GOOGLE_CLIENT_ID = '665289826545-463bhl103plveo5l05b94vjnenmr3f7e';

  // Subject to notify when Google is ready
  public googleReady$ = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private rendererFactory: RendererFactory2,
    private appRef: ApplicationRef,
    private zone: NgZone,
    @Optional() private scriptService?: ScriptService
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);

    SocialLoginComponent.loginStatus$
      .pipe(distinctUntilChanged())
      .subscribe((status) => this.runGlobalChangeDetection(status));
  }

  private runGlobalChangeDetection(status?: boolean): void {
    console.log('[IMGoing] login state changed → forcing repaint (no reload). isLoggedIn =', status);

    const tick = () => {
      try {
        this.appRef.tick();
      } catch {
      }
      try {
        (window as any).IMGoing?.forceChangeDetection?.();
      } catch {
      }
    };

    this.zone.runOutsideAngular(() => {
      setTimeout(tick, 0);
      setTimeout(tick, 150);
    });
  }

  checkGoogleLoginStatus(): boolean {
    const stored = localStorage.getItem('social.login.googleUser');
    if (stored) {
      const user = JSON.parse(stored);
      this.applyGoogleUser(user);
    }
    return this.isUserLoggedIn;
  }

  checkFacebookLoginStatus(): boolean {
    const stored = localStorage.getItem('social.login.facebookUser');
    if (stored) {
      const user = JSON.parse(stored);
      this.applyFacebookUser(user);
    }
    return this.isUserLoggedIn;
  }

  applyGoogleUser(user: {
    userId?: string;
    profilePicture?: string;
    email?: string;
    userName?: string;
  }): void {
    if (!user) return;
    this.isUserLoggedIn = true;
    this.profilePicture = user.profilePicture || '';
    this.provider = 'google';
    if (user.userId) {
      localStorage.setItem('social.login.googleUser', JSON.stringify(user));
    }
    SocialLoginComponent.loginStatus$.next(true);
  }

  applyFacebookUser(user: { userId?: string; profilePicture?: string }): void {
    if (!user) return;
    this.isUserLoggedIn = true;
    this.profilePicture = user.profilePicture || '';
    this.provider = 'facebook';
    if (user.userId) {
      localStorage.setItem(
        'social.login.facebookUser',
        JSON.stringify({
          userId: user.userId,
          profilePicture: user.profilePicture,
        })
      );
    }
    SocialLoginComponent.loginStatus$.next(true);
  }

  // Load Google library
  loadGoogleLibrary(): Promise<void> {
    return new Promise((resolve, reject) => {
      // In WebView, we don't need to load the Google script as the app handles OAuth
      // But we still need to mark it as ready so the button component can render
      if (this.isInWebView()) {
        this.googleReady$.next(true);
        resolve();
        return;
      }

      // Skip if already loaded
      if (this.googleLibraryLoaded) {
        this.googleReady$.next(true);
        resolve();
        return;
      }

      if ((window as any).google?.accounts?.id) {
        this.googleLibraryLoaded = true;
        this.googleReady$.next(true);
        resolve();
        return;
      }

      const loadScript = () => {
        // Use ScriptService if available, otherwise use ScriptLoader utility
        if (this.scriptService) {
          if (!this.isGsiScriptAlreadyPresent()) {
            const scriptElement = this.scriptService.loadJsScript(
              this.renderer,
              this.GOOGLE_SCRIPT_PATH
            );

            scriptElement.onload = () => {
              console.log('Google login script loaded');
              this.googleLibraryLoaded = true;
              this.googleReady$.next(true);
              resolve();
            };

            scriptElement.onerror = () => {
              console.error('Could not load the Google script');
              reject(new Error('Failed to load Google script'));
            };
          } else {
            this.waitForGsi(() => {
              this.googleLibraryLoaded = true;
              this.googleReady$.next(true);
              resolve();
            }, () => reject(new Error('GSI did not become ready')));
          }
        } else {
          // Fallback to ScriptLoader utility
          ScriptLoader.loadScript(this.GOOGLE_SCRIPT_PATH)
            .then(() => {
              console.log('Google login script loaded');
              this.googleLibraryLoaded = true;
              this.googleReady$.next(true);
              resolve();
            })
            .catch((error) => {
              console.error('Could not load the Google script');
              reject(error);
            });
        }
      };

      loadScript();
    });
  }

  private isGsiScriptAlreadyPresent(): boolean {
    if (this.scriptService?.isScriptExist(this.GOOGLE_SCRIPT_PATH)) {
      return true;
    }
    const scripts = this.document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].getAttribute('src') || '';
      if (src.split('?')[0] === this.GOOGLE_SCRIPT_PATH) {
        return true;
      }
    }
    return false;
  }

  private waitForGsi(onReady: () => void, onTimeout: () => void, attempt = 0) {
    if ((window as any).google?.accounts?.id) {
      onReady();
      return;
    }
    if (attempt > 30) {
      onTimeout();
      return;
    }
    setTimeout(() => this.waitForGsi(onReady, onTimeout, attempt + 1), 500);
  }

  // Initialize Google Sign-In
  async initializeGoogle(): Promise<void> {
    // Prevent multiple initializations
    if (this.isGoogleInitialized) {
      console.log('Google already initialized');
      this.renderGoogleButtons();
      this.performGoogleInitialization();
      return;
    }

    try {
      // Load library if needed
      if (!this.googleLibraryLoaded) {
        await this.loadGoogleLibrary();
        this.performGoogleInitialization();
      }

      if (this.isInWebView()) {
        // In webview, just check login status and render custom button
        this.checkGoogleLoginStatus();
        //setTimeout(() => this.renderCustomGoogleButton(), 100);
        this.performGoogleInitialization();
      } else {
        // Initialize Google Sign-In
        this.performGoogleInitialization();
      }
    } catch (error) {
      console.error('Error initializing Google:', error);
    }
  }

  private async performGoogleInitialization(): Promise<void> {
    // Prevent multiple initializations
    if (this.isGoogleInitialized) {
      console.log('CheckLoginStatusService: Google already initialized, skipping');
      this.renderGoogleButtons();
      return;
    }

    console.log('CheckLoginStatusService: Initializing Google Sign-In');
    await this.loadGoogleLibrary();

    this.checkGoogleLoginStatus();

    const google = (window as any).google;

    if (google?.accounts?.id?.initialize) {
      try {
        if (!(window as any).__igGisInitialized) {
          google.accounts.id.initialize({
            client_id: this.GOOGLE_CLIENT_ID,
            callback: (response: any) => {
              this.sendLoginRequest('google');
              this.handleGoogleSignIn(response);
            },
            cancel_on_tap_outside: false,
            auto_select: false, 
          });
          (window as any).__igGisInitialized = true;
        }

        this.isGoogleInitialized = true;

        // Render buttons after a short delay to ensure DOM is ready
        setTimeout(() => {
          this.renderGoogleButtons();
        }, 100);
      } catch (error) {
        console.error('Failed to initialize Google Sign-In:', error);
      }
    } else {
      console.error('Google Sign-In library not loaded');
    }
  }

  private handleGoogleSignIn(response: any): void {
    console.log('Google sign-in response:', response);

    if (response.credential) {
      const credential: any = jwt_decode(response.credential);
      console.log('Decoded credential:', credential);

      this.applyGoogleUser({
        userId: credential.email + '|google',
        profilePicture: credential.picture,
        email: credential.email,
        userName: credential.name,
      });
    }
  }

  private getShadowRoot() {
    return this.document.querySelector('app-root')?.shadowRoot;
  }

  // Render Google Sign-In buttons
  renderGoogleButtons(container?: HTMLElement | ShadowRoot): void {
    const google = (window as any).google;

    if (!google?.accounts?.id?.renderButton) {
      console.warn('Google Sign-In renderButton not available');
      return;
    }

    console.log('CheckLoginStatusService: Rendering Google buttons...');

    const buttonContainers = (container || this.getShadowRoot() || this.document).querySelectorAll(
      '.google-signin-btn'
    );
    const popupButtons = this.document.querySelectorAll(
      '.cdk-overlay-container .google-signin-btn'
    );
    const modalButtons = this.document.querySelectorAll('.login-popup .google-signin-btn');

    // Check for the specific popup button container
    const popupButtonContainer = this.document.querySelector('#google-signin-btn-popup');

    const allButtons = Array.from(buttonContainers)
      .concat(Array.from(popupButtons))
      .concat(Array.from(modalButtons));

    // Add the specific popup container if it exists
    if (popupButtonContainer) {
      allButtons.push(popupButtonContainer);
    }

    console.log('CheckLoginStatusService: Found Google button containers:', allButtons.length);

    allButtons.forEach((element) => {
      if (!element.querySelector('.abcRioButton')) {
        // Check if button already rendered
        console.log('CheckLoginStatusService: Rendering Google button in container:', element);
        google.accounts.id.renderButton(element, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width: window.innerWidth >= 400 ? 295 : 260,
          text: 'signin_with',
          shape: 'rectangular',
        });
      } else {
        console.log(
          'CheckLoginStatusService: Google button already rendered in container:',
          element
        );
      }
    });
  }

  // Render custom button for webview
  private renderCustomGoogleButton(): void {
    const buttonContainers = this.document.querySelectorAll('.google-signin-btn');

    buttonContainers.forEach((container: Element) => {
      if (!container.querySelector('.custom-google-btn')) {
        const button = this.document.createElement('button');
        button.className = 'custom-google-btn';
        button.innerHTML = `
          <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" style="margin-right: 8px; vertical-align: middle;">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          <span style="font-family: 'Roboto', arial, sans-serif; font-size: 14px; color: #3c4043; font-weight: 500;">Sign in with Google</span>
        `;
        button.style.cssText = `
          background-color: #fff;
          border: 1px solid #dadce0;
          border-radius: 4px;
          padding: 11px 24px;
          display: inline-flex;
          align-items: center;
          cursor: pointer;
          font-family: 'Roboto', arial, sans-serif;
          transition: background-color .218s, border-color .218s;
          width: 100%;
          max-width: 295px;
        `;

        button.onmouseover = () => {
          button.style.backgroundColor = '#f8f9fa';
          button.style.borderColor = '#dadce0';
        };

        button.onmouseout = () => {
          button.style.backgroundColor = '#fff';
          button.style.borderColor = '#dadce0';
        };

        button.onclick = () => {
          this.sendLoginRequest('google');
          this.performGoogleInitialization();
        };

        container.appendChild(button);
      }
    });
  }

  // Send login request via postMessage
  private sendLoginRequest(provider: 'google' | 'facebook'): void {
    const message = {
      type: 'APP_LOGIN_REQUEST',
      provider: provider,
    };

    const host = window.parent && window.parent !== window ? window.parent : window;
    console.log('Sending login request:', message);
    host.postMessage(message, '*');
  }

  // Logout methods
  logoutGoogle(): void {
    this.sendLogoutRequest('google');
    const google = (window as any).google;
    if (google?.accounts?.id?.disableAutoSelect) {
      google.accounts.id.disableAutoSelect();
    }
    this.clearGoogleData();
  }

  logoutFacebook(): void {
    this.sendLogoutRequest('facebook');
    this.clearFacebookData();
  }

  private sendLogoutRequest(provider: 'google' | 'facebook'): void {
    const message = {
      type: 'APP_LOGOUT_REQUEST',
      provider: provider,
    };

    const host = window.parent && window.parent !== window ? window.parent : window;
    console.log('Sending logout request:', message);
    host.postMessage(message, '*');
  }

  private clearGoogleData(): void {
    localStorage.removeItem('social.login.googleUser');
    if (this.provider === 'google' || !this.provider) {
      this.isUserLoggedIn = false;
      this.profilePicture = '';
      this.provider = '';
    }
    if (SocialLoginComponent.loginStatus$.getValue()) {
      SocialLoginComponent.loginStatus$.next(false);
    }
  }

  private clearFacebookData(): void {
    localStorage.removeItem('social.login.facebookUser');
    if (this.provider === 'facebook' || !this.provider) {
      this.isUserLoggedIn = false;
      this.profilePicture = '';
      this.provider = '';
    }
    if (SocialLoginComponent.loginStatus$.getValue()) {
      SocialLoginComponent.loginStatus$.next(false);
    }
  }

  // Utility methods
  private isInWebView(): boolean {
    return (
      window.parent !== window ||
      navigator.userAgent.includes('wv') || // Android WebView
      (navigator.userAgent.includes('Version/') && navigator.userAgent.includes('Mobile/'))
    ); // iOS WebView
  }

  // Update from app login (for webview integration)
  updateFromAppLogin(loginData: any): void {
    if (loginData.provider === 'google') {
      this.applyGoogleUser({
        userId: loginData.userId,
        profilePicture: loginData.profilePicture,
        email: loginData.email,
        userName: loginData.userName,
      });
    } else if (loginData.provider === 'facebook') {
      this.applyFacebookUser({
        userId: loginData.userId,
        profilePicture: loginData.profilePicture,
      });
    }
  }
}
